import {
    Button,
    DrawerProps,
    Group,
    Select,
    Stack,
} from '@mantine/core';
import {PlatformDrawer} from '@flexinet/ui-components';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    ReportTypesFilters,
    useReportTypesFilter,
} from '../hooks/useReportTypesFilter';

/**
 * FNP-918: the report types filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (useReportTypesFilter), not component state, so the table
 * — a sibling, not a child — filters the same list.
 *
 * ⚠️ Only `livemode` is offered, and it is the only field on a ReportType worth
 * a control. Telling live report types from test ones is a real question with
 * exactly two answers, which is what a Select is for. `name`, `id` and
 * `defaultColumns` are all free text already covered by the keyword box, and
 * `version` is a number an operator reads rather than filters on.
 *
 * ⚠️ Unlike the paginated modules, filtering here covers the WHOLE catalogue —
 * `listReportTypes` returns everything in one response, so no rows go
 * unexamined. See useReportTypes.ts.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useReportTypesFilter();

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Report Types')}
            size='md'
            {...rest}
        >
            <Formik<ReportTypesFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Mode')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'true',
                                        label: I18n.get('Live'),
                                    },
                                    {
                                        value: 'false',
                                        label: I18n.get('Test'),
                                    },
                                ]}
                                value={values.livemode ?? null}
                                onChange={(value) =>
                                    setFieldValue('livemode', value ?? '')}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </PlatformDrawer>
    );
};
